import React from "react";
import { Button, Card, Col, Form, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from 'react-router-dom';
import Select from 'react-select'
import './dataPanelTab.css';
import DataPanelItemCard from "./dataPanelItemCard";
import FeatherIcon from 'feather-icons-react';
import i18n from "../../../../../../_translations/i18n";
import { PREPLAN_FIELD_MAPPINGS, DATA_PANEL_DATA_FIELDS, NUMERIC_FIELDS, TEXT_FIELDS, CODE_FIELDS, BOOLEAN_FIELDS, DATE_FIELDS, NON_SORTABLE_FIELDS, getFieldNameForCardType } from './dataPanelFilterOptions.data';
import { setDataPanelColumnSelection } from '../../../../../../actions/simulateAllActions';

class DataPanelTabContent extends React.Component {

    constructor(props) {
        super(props)
        this._isMounted = false;
        this.state = {
            defaultOption: this.props.itemSort[0],
            containerWidth: 0
        }
    }

    componentDidMount() {
        this.initializeResizeObserver();
        this.truncateHeaderText();
    }

    componentDidUpdate(prevProps, prevState) {
        this.truncateHeaderText();
    }

    truncateHeaderText = () => {
        const headerCols = document.querySelectorAll('.data-panel-filter-col');

        headerCols.forEach(col => {
            const allChildren = col.querySelectorAll('*');

            allChildren.forEach(child => {
                const walker = document.createTreeWalker(
                    child,
                    NodeFilter.SHOW_TEXT,
                    null,
                    false
                );

                let node;
                while (node = walker.nextNode()) {
                    const text = node.textContent.trim();
                    if (text.length > 3) {
                        node.textContent = text.substring(0, 3) + '...';
                    }
                }
            });
        });
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    initializeResizeObserver = () => {
        const element = document.querySelector('.simulation-filter-section');
        if (element) {
            this.resizeObserver = new ResizeObserver(entries => {
                for (let entry of entries) {
                    if (entry.target === element) {
                        setTimeout(() => {
                            const containerWidth = entry.contentRect.width;
                            this.setState({ containerWidth });

                        }, 100);
                    }
                }
            });
            this.resizeObserver.observe(element);
        }
    }

    handleFilterOptionClick = (option) => {
        const currentTab = this.props.content || 'product';
        const currentSelections = this.props.selectedFilters || {};

        if (currentSelections[option.short]) {
            // Clear selection
            this.props.setDataPanelColumnSelection(currentTab, {});
        } else {
            // Set new selection
            this.props.setDataPanelColumnSelection(currentTab, { [option.short]: true });
        }
    }

    // Get Lambda data value for sorting
    getLambdaValueForSort = (product, filterKey, cardType, isToggleOn = false) => {
        // Use shared field mappings
        const fieldName = getFieldNameForCardType(filterKey, cardType);
        if (!fieldName) return 0;

        let value = null;

        // Check if it's a dataPanelData field
        if (DATA_PANEL_DATA_FIELDS.includes(filterKey)) {
            if (isToggleOn) {
                // When toggle is ON, check normalizedData first
                let normalizedData = null;
                if (cardType === 'product') {
                    normalizedData = product?.normalizedLambdaData;
                } else if (cardType === 'brand') {
                    normalizedData = product?.brandNormalizedLambdaData;
                } else if (cardType === 'supplier') {
                    normalizedData = product?.supplierNormalizedLambdaData;
                } else if (cardType === 'category') {
                    normalizedData = product?.categoryNormalizedLambdaData;
                } else if (cardType === 'sub-category') {
                    normalizedData = product?.subCategoryNormalizedLambdaData;
                }
                
                if (normalizedData && normalizedData[fieldName] !== undefined) {
                    value = normalizedData[fieldName];
                } else {
                    // Fallback to dataPanelData if not in normalizedData
                    let dataPanelData = null;
                    if (cardType === 'product') {
                        dataPanelData = product?.dataPanelData;
                    } else if (cardType === 'brand') {
                        dataPanelData = product?.brandDataPanelData;
                    } else if (cardType === 'supplier') {
                        dataPanelData = product?.supplierDataPanelData;
                    } else if (cardType === 'category') {
                        dataPanelData = product?.categoryDataPanelData;
                    } else if (cardType === 'sub-category') {
                        dataPanelData = product?.subCategoryDataPanelData;
                    }

                    if (!dataPanelData || dataPanelData[fieldName] === undefined) return '';
                    value = dataPanelData[fieldName];
                }
            } else {
                // When toggle is OFF, use dataPanelData
                let dataPanelData = null;
                if (cardType === 'product') {
                    dataPanelData = product?.dataPanelData;
                } else if (cardType === 'brand') {
                    dataPanelData = product?.brandDataPanelData;
                } else if (cardType === 'supplier') {
                    dataPanelData = product?.supplierDataPanelData;
                } else if (cardType === 'category') {
                    dataPanelData = product?.categoryDataPanelData;
                } else if (cardType === 'sub-category') {
                    dataPanelData = product?.subCategoryDataPanelData;
                }

                if (!dataPanelData || dataPanelData[fieldName] === undefined) return '';
                value = dataPanelData[fieldName];
            }
        } else {
            // Handle Lambda data fields (preplan/normalized)
            let lambdaData = null;
            if (cardType === 'product') {
                lambdaData = product?.lambdaData;
            } else if (cardType === 'brand') {
                lambdaData = product?.brandLambdaData;
            } else if (cardType === 'supplier') {
                lambdaData = product?.supplierLambdaData;
            } else if (cardType === 'category') {
                lambdaData = product?.categoryLambdaData;
            } else if (cardType === 'sub-category') {
                lambdaData = product?.subCategoryLambdaData;
            }

            if (!lambdaData || lambdaData[fieldName] === undefined) return '';
            value = lambdaData[fieldName];
        }

        // For date fields, return timestamp for sorting
        if (fieldName === 'arigoCreationDate') {
            return value ? new Date(value).getTime() : 0;
        }

        // Return the actual value (numeric, string, boolean, etc.)
        return value ?? '';
    }

    // Sort products array based on current sort configuration
    getSortedProducts = (products, cardType) => {
        const sortConfig = this.props.sortConfig || { key: null, direction: 'desc' };

        if (!sortConfig.key) {
            // Default sort by 'now' value descending
            return [...products].sort((a, b) => {
                let aNow = 0, bNow = 0;
                if (cardType === 'product') {
                    aNow = a.now ?? 0;
                    bNow = b.now ?? 0;
                } else if (cardType === 'brand') {
                    aNow = a.brandNow ?? 0;
                    bNow = b.brandNow ?? 0;
                } else if (cardType === 'sub-category') {
                    aNow = a.subCatNow ?? 0;
                    bNow = b.subCatNow ?? 0;
                } else if (cardType === 'category') {
                    aNow = a.categoryNow ?? 0;
                    bNow = b.categoryNow ?? 0;
                } else if (cardType === 'supplier') {
                    aNow = a.supplierNow ?? 0;
                    bNow = b.supplierNow ?? 0;
                }
                return bNow - aNow; // Descending
            });
        }

        // Sort by Lambda data column
        return [...products].sort((a, b) => {
            const aValue = this.getLambdaValueForSort(a, sortConfig.key, cardType, this.props.isToggleOn);
            const bValue = this.getLambdaValueForSort(b, sortConfig.key, cardType, this.props.isToggleOn);

            // Check if this is a numeric field (not code, text, boolean, or date)
            const isNumericField = NUMERIC_FIELDS.includes(sortConfig.key);
            const isTextField = TEXT_FIELDS.includes(sortConfig.key);
            const isCodeField = CODE_FIELDS.includes(sortConfig.key);
            const isBooleanField = BOOLEAN_FIELDS.includes(sortConfig.key);
            const isDateField = DATE_FIELDS.includes(sortConfig.key);

            // Numeric fields: desc = highest first, asc = lowest first
            if (isNumericField) {
                const aNum = typeof aValue === 'number' ? aValue : 0;
                const bNum = typeof bValue === 'number' ? bValue : 0;

                if (sortConfig.direction === 'desc') {
                    return bNum - aNum; // Highest first
                } else {
                    return aNum - bNum; // Lowest first
                }
            }
            // Text, Code, Boolean, Date fields: desc = A-Z, asc = Z-A
            else if (isTextField || isCodeField || isBooleanField || isDateField) {
                const aStr = String(aValue ?? '').toLowerCase();
                const bStr = String(bValue ?? '').toLowerCase();

                if (sortConfig.direction === 'desc') {
                    return aStr.localeCompare(bStr); // A-Z
                } else {
                    return bStr.localeCompare(aStr); // Z-A
                }
            }
            // Fallback: treat as alphanumeric (desc = A-Z, asc = Z-A)
            else {
                const aStr = String(aValue ?? '').toLowerCase();
                const bStr = String(bValue ?? '').toLowerCase();

                if (sortConfig.direction === 'desc') {
                    return aStr.localeCompare(bStr); // A-Z
                } else {
                    return bStr.localeCompare(aStr); // Z-A
                }
            }
        });
    }

    // hangableFilter = (event) => {
    //     const value = event.target.value;
    //     console.log('value_', value);
    // }

    

    render() {
        const { defaultOption, containerWidth } = this.state
        const { toSimulateProductonly, formValue, isToggleOn, isLoadingLambdaDataPanel, selectedFilters } = this.props
        // console.log('toSimulateProductonly', toSimulateProductonly);


        // Remove duplicates based on the brand name when content is "brand"
        let uniqueBrands = toSimulateProductonly;
        if (this.props.content === "brand" && toSimulateProductonly) {
            uniqueBrands = Array.from(new Map(toSimulateProductonly.map(prod => {
                const key = `${prod?.brand?.brandName}-${prod?.subCat?.subCategoryName}`;
                return [key, prod];
            })).values());


            // Add noproduct brand hierarchy items from endpoint (only if not in search or filter mode)
            if (this.props.hierarchyData?.noproductBrands && !this.props.isInSearchMode && !this.props.isInFilterMode) {
                this.props.hierarchyData.noproductBrands.forEach(brand => {
                    // Extract subcategory info - for noproduct brands, subcategoryName is at root level
                    const subcategoryName = brand.subcategoryName || brand.lambdaData?.subcategoryName;
                    const subCategoryId = brand.subCategoryId || brand.lambdaData?.subCategoryId;

                    // Check for existing brand by brandId and subcategoryName combination
                    const existingBrandById = uniqueBrands.find(prod =>
                        prod?.brand?.brandid === brand.brandId &&
                        prod?.subCat?.subCategoryName === subcategoryName
                    );

                    // Also check if this noproduct brand is already added
                    const alreadyAddedNoproduct = uniqueBrands.find(prod =>
                        prod?.type === 'noproduct-brand' &&
                        prod?.brand?.brandid === brand.brandId &&
                        prod?.subCat?.subCategoryName === subcategoryName
                    );

                    if (!existingBrandById && !alreadyAddedNoproduct) {
                        // Create a product-like structure for the noproduct brand
                        const brandIdKey = `${brand.brandId}-${subcategoryName}`;

                        const noproductBrand = {
                            type: 'noproduct-brand',
                            brand: {
                                brandName: brand.brandName,
                                brandid: brand.brandId
                            },
                            subCat: {
                                subCategoryName: subcategoryName,
                                subCategoryid: subCategoryId
                            },
                            brandLambdaData: brand.lambdaData,
                            brandNormalizedLambdaData: brand.normalizedLambdaData,
                            brandDataPanelData: brand.dataPanelData,
                            brandNow: 0,
                            brandRec: parseFloat((parseFloat(brand.lambdaData?.allocatedShareBrand) || 0).toFixed(2)),
                            barcode: `noproduct-brand-${brandIdKey}`,
                            name: brand.brandName
                        };

                        uniqueBrands.push(noproductBrand);
                    }
                });
            }
        }

        // Remove duplicates based on the subCat name when content is "sub-category"
        let uniqueSubCat = toSimulateProductonly;
        if (this.props.content === "sub-category" && toSimulateProductonly) {
            uniqueSubCat = Array.from(new Map(toSimulateProductonly.map(prod => [prod?.subCat?.subCategoryName, prod])).values());

            // Add noproduct subcategory hierarchy items from endpoint (only if not in search or filter mode)
            if (this.props.hierarchyData?.noproductSubcategories && !this.props.isInSearchMode && !this.props.isInFilterMode) {
                this.props.hierarchyData.noproductSubcategories.forEach(subcategory => {
                    // Check for existing subcategory by ID (more reliable than name)
                    const existingSubCatById = uniqueSubCat.find(prod => 
                        prod?.subCat?.subCategoryid === subcategory.subCategoryId
                    );
                    
                    // Also check if this noproduct subcategory is already added
                    const alreadyAddedNoproduct = uniqueSubCat.find(prod => 
                        prod?.type === 'noproduct-subcategory' && 
                        prod?.subCat?.subCategoryid === subcategory.subCategoryId
                    );
                    
                    if (!existingSubCatById && !alreadyAddedNoproduct) {
                        // Create a product-like structure for the noproduct subcategory
                        uniqueSubCat.push({
                            type: 'noproduct-subcategory',
                            subCat: {
                                subCategoryName: subcategory.subcategoryName,
                                subCategoryid: subcategory.subCategoryId
                            },
                            categoryId: subcategory.lambdaData?.categoryId,
                            categoryName: subcategory.lambdaData?.categoryName,
                            subCategoryLambdaData: subcategory.lambdaData,
                            subCategoryNormalizedLambdaData: subcategory.normalizedLambdaData,
                            subCategoryDataPanelData: subcategory.dataPanelData,
                            subCatNow: 0,
                            subCatRec: parseFloat((parseFloat(subcategory.lambdaData?.allocatedShareSubcategory) || 0).toFixed(2)),
                            barcode: `noproduct-subcat-${subcategory.subCategoryId}`,
                            name: subcategory.subcategoryName
                        });
                    }
                });
            }
        }

        let uniqueCategory = toSimulateProductonly;
        if (this.props.content === "category" && toSimulateProductonly) {
            uniqueCategory = Array.from(
                new Map(
                    toSimulateProductonly
                        .filter(prod => prod?.categoryName && prod.categoryName !== "")
                        .map(prod => [prod.categoryName, prod])
                ).values()
            );

            // Add noproduct category hierarchy items from endpoint (only if not in search or filter mode)
            if (this.props.hierarchyData?.noproductCategories && !this.props.isInSearchMode && !this.props.isInFilterMode) {
                this.props.hierarchyData.noproductCategories.forEach(category => {
                    // Check for existing category by ID (more reliable than name)
                    const existingCategoryById = uniqueCategory.find(prod => 
                        prod?.categoryId === category.categoryId
                    );
                    
                    // Also check if this noproduct category is already added
                    const alreadyAddedNoproduct = uniqueCategory.find(prod => 
                        prod?.type === 'noproduct-category' && 
                        prod?.categoryId === category.categoryId
                    );
                    
                    if (!existingCategoryById && !alreadyAddedNoproduct) {
                        // Create a product-like structure for the noproduct category
                        uniqueCategory.push({
                            type: 'noproduct-category',
                            categoryId: category.categoryId,
                            categoryName: category.categoryName,
                            categoryLambdaData: category.lambdaData,
                            categoryNormalizedLambdaData: category.normalizedLambdaData,
                            categoryDataPanelData: category.dataPanelData,
                            categoryNow: 0,
                            categoryRec: parseFloat((parseFloat(category.lambdaData?.allocatedShareCategory) || 0).toFixed(2)),
                            barcode: `noproduct-cat-${category.categoryId}`,
                            name: category.categoryName
                        });
                    }
                });
            }
        }

        // Remove duplicates based on the supplier name + subcategory combination when content is "supplier"
        let uniqueSupplier = toSimulateProductonly;
        if (this.props.content === "supplier" && toSimulateProductonly) {
            uniqueSupplier = Array.from(new Map(toSimulateProductonly.map(prod => {
                const key = `${prod?.supplier?.supplierName}-${prod?.subCat?.subCategoryName}`;
                return [key, prod];
            })).values());

            // Add noproduct supplier hierarchy items from endpoint (only if not in search or filter mode)
            if (this.props.hierarchyData?.noproductSuppliers && !this.props.isInSearchMode && !this.props.isInFilterMode) {
                this.props.hierarchyData.noproductSuppliers.forEach(supplier => {
                    // Check for existing supplier by ID (more reliable than name)
                    const existingSupplierById = uniqueSupplier.find(prod => 
                        prod?.supplier?.supplierId === supplier.supplierId
                    );
                    
                    // Also check if this noproduct supplier is already added
                    const alreadyAddedNoproduct = uniqueSupplier.find(prod => 
                        prod?.type === 'noproduct-supplier' && 
                        prod?.supplier?.supplierId === supplier.supplierId
                    );
                    
                    if (!existingSupplierById && !alreadyAddedNoproduct) {
                        // Create a product-like structure for the noproduct supplier
                        // Extract subcategory info - for noproduct suppliers, subcategoryName is at root level
                        const subcategoryName = supplier.subcategoryName || supplier.lambdaData?.subcategoryName;
                        const subCategoryId = supplier.subCategoryId || supplier.lambdaData?.subCategoryId;

                        uniqueSupplier.push({
                            type: 'noproduct-supplier',
                            supplier: {
                                supplierName: supplier.supplierName,
                                supplierId: supplier.supplierId,
                                supplierCode: supplier.supplierCode
                            },
                            subCat: {
                                subCategoryName: subcategoryName,
                                subCategoryid: subCategoryId
                            },
                            supplierLambdaData: supplier.lambdaData,
                            supplierNormalizedLambdaData: supplier.normalizedLambdaData,
                            supplierDataPanelData: supplier.dataPanelData,
                            supplierNow: 0,
                            supplierRec: parseFloat((parseFloat(supplier.lambdaData?.allocatedShareSupplier) || 0).toFixed(2)),
                            barcode: `noproduct-supplier-${supplier.supplierId}`,
                            name: supplier.supplierName
                        });
                    }
                });
            }
        }

        // Apply sorting based on current sort configuration
        let sortedProducts = this.getSortedProducts(toSimulateProductonly || [], 'product');
        let sortedBrands = this.getSortedProducts(uniqueBrands || [], 'brand');
        let sortedSubCat = this.getSortedProducts(uniqueSubCat || [], 'sub-category');
        let sortedCategory = this.getSortedProducts(uniqueCategory || [], 'category');
        let sortedSupplier = this.getSortedProducts(uniqueSupplier || [], 'supplier');

        // console.log('sortedcategory', sortedCategory);
        // console.log('sortedSupplier', sortedSupplier);
        
        const isScaled = window.devicePixelRatio && window.devicePixelRatio > 1.01;
        const firstColBorder = i18n.dir() === "rtl" ? { borderLeft: '1px solid #e5e7eb' }: { borderRight: '1px solid #e5e7eb'}
        const middleColBorder = i18n.dir() === "rtl" ? { borderLeft: '1px solid #e5e7eb' }: { borderRight: '1px solid #e5e7eb'}
        const lastColBorder = i18n.dir() === "rtl" ? { borderRight: 'none' } : { borderLeft: 'none'}

        const valueToPass = formValue || 0;
        return (
                <div className="data-panel-table-container">
                    <div className="data-panel-columns">
                        <div className="data-panel-header-col" style={{...firstColBorder, flex: this.props.content === 'product' ? 2 : 1.5}}>
                            {
                                <Form.Group as={Row} >
                                    
                                        <Form.Label style={{marginBottom: 0, color: this.props.dmode ? '#FFFFFF' : '#111827'}}>
                                            {this.props.t('BY_FACE')}
                                        </Form.Label>
                                    
                                </Form.Group>
                            }
                        </div>

                        {this.props.filterSelections && this.props.filterSelections.length > 0 && (
                            this.props.filterSelections.map((option, idx) => (
                                <div
                                    key={option.short}
                                    className="data-panel-header-col data-panel-filter-col"
                                    style={{...middleColBorder, flex: 1}}
                                >
                                    {/* <Form.Group as={Row}> */}
                                        
                                            <Form.Label style={{
                                                display: 'flex', 
                                                flexDirection: 'row', 
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                                color: selectedFilters[option.short] ? 
                                                    (this.props.dmode ? '#2CC990' : '#5128A0') : 
                                                    (this.props.dmode ? '#FFFFFF' : '#111827'),
                                                fontWeight: selectedFilters[option.short] ? 'bold' : 'normal',
                                                marginBottom: 0,
                                            }}>
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`tooltip-${option.short}`}>
                                                            {option.label}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span style={{fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 80}}>{option.displayShort || option.short}</span>
                                                </OverlayTrigger>
                                                <span 
                                                    className="filter-option-icon" 
                                                    style={{
                                                        cursor: NON_SORTABLE_FIELDS.includes(option.short) ? 'default' : 'pointer',
                                                        backgroundColor: (selectedFilters[option.short] || this.props.sortConfig?.key === option.short) ?
                                                            (this.props.dmode ? '#2CC9901A' : '#9B4EDF1A') : 'transparent',
                                                        borderRadius: '3px',
                                                        paddingBottom: this.props.isRTL === 'rtl' ? '4px' : '0px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        border: this.props.sortConfig?.key === option.short
                                                            ? `1px solid ${this.props.dmode ? '#2CC990' : '#5128A0'}`
                                                            : '1px solid transparent',
                                                        transition: 'all 0.2s ease',
                                                        opacity: NON_SORTABLE_FIELDS.includes(option.short) ? 0.4 : 1
                                                    }}
                                                    onClick={NON_SORTABLE_FIELDS.includes(option.short) ? undefined : (e) => {
                                                        e.stopPropagation();
                                                        this.props.handleColumnSort && this.props.handleColumnSort(option);
                                                    }}
                                                >
                                                    <FeatherIcon
                                                        icon="chevron-up"
                                                        size={10}
                                                        style={{
                                                            cursor: NON_SORTABLE_FIELDS.includes(option.short) ? 'default' : 'pointer',
                                                            color: NON_SORTABLE_FIELDS.includes(option.short)
                                                                ? (this.props.dmode ? '#FFFFFF30' : '#11182730')
                                                                : this.props.sortConfig?.key === option.short && this.props.sortConfig?.direction === 'asc'
                                                                    ? (this.props.dmode ? '#2CC990' : '#5128A0')
                                                                    : (this.props.dmode ? '#FFFFFF50' : '#11182750'),
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    />
                                                    <FeatherIcon
                                                        icon="chevron-down"
                                                        size={10}
                                                        style={{
                                                            cursor: NON_SORTABLE_FIELDS.includes(option.short) ? 'default' : 'pointer',
                                                            color: NON_SORTABLE_FIELDS.includes(option.short)
                                                                ? (this.props.dmode ? '#FFFFFF30' : '#11182730')
                                                                : this.props.sortConfig?.key === option.short && this.props.sortConfig?.direction === 'desc'
                                                                    ? (this.props.dmode ? '#2CC990' : '#5128A0')
                                                                    : (this.props.dmode ? '#FFFFFF50' : '#11182750'),
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    />
                                                </span>
                                            </Form.Label>
                                        
                                    {/* </Form.Group> */}
                                </div>
                            ))
                        )}
                        
                        {/* {this.props.content === "product" && (
                            <Col xs={5} className="d-flex justify-content-start align-items-center">
                                <Form.Group as={Row} controlId="formHangable">
                                    <Form.Label column xs="auto">{this.props.t('HANGABLE')}</Form.Label>
                                        <Col>
                                            <Form.Select aria-label="Default select example" size="sm" className="hangable-select" onChange={this.props.hangableFilter} defaultValue="all">
                                                <option value={'all'}>{this.props.t('ALL')}</option>
                                                <option value={true}>{this.props.t('HANGABLE')}</option>
                                                <option value={false}>{this.props.t('NON_HANGABLE')}</option>
                                            </Form.Select>
                                        </Col>
                                </Form.Group>
                            </Col>
                        )} */}
                        {/* <Col className={`d-flex justify-content-end  ${this.props.isRTL === "rtl" ? "left-corner" : "right-corner"}`}>
                            <Form.Group style={{ paddingTop: "5px" }} as={Row} controlId="formNowRec">
                                <Col
                                    xs={7}
                                >
                                    {this.props.content !== "supplier" && (
                                        <Form.Label style={{ fontSize: '12px', paddingRight: '10px' }}>
                                            {this.props.t("NOW")}
                                        </Form.Label>
                                    )}
                                </Col>
                                <Col xs={5} >
                                    {this.props.content !== "supplier" && (
                                        <Form.Label style={{ fontSize: '12px' }}>
                                            {this.props.t("DATA_PANEL.rec")}
                                        </Form.Label>
                                    )}
                                </Col>
                            </Form.Group>
                        </Col> */}
                      <div className="data-panel-header-col" style={{...lastColBorder, flex: 1.2}}>
                        <Form.Group as={Row} controlId="formNowRec">
                            {
                            <OverlayTrigger
                                placement="top" 
                                overlay={
                                <Tooltip id="tooltip-now-rec" dir={this.props.isRTL === "rtl" ? "rtl" : "ltr"}>
                                    {this.props.t('Now_Rec')} 
                                </Tooltip>
                                }
                            >
                                <Form.Label style={{ marginBottom: 0, color: this.props.dmode ? '#FFFFFF' : '#111827' }}>
                                    {this.props.t('Now_Rec_Short')}
                                </Form.Label>
                            </OverlayTrigger>
                            }
                        </Form.Group>
                        </div>
                    </div>
                    <Row
                        className="v2-productBox"
                        style={{
                            height: this.props.simType === 'AUI' ? this.props.isRTL === "rtl"? this.props.catDivHeight - 280 : this.props.catDivHeight - 260 : this.props.catDivHeight,
                            overflow: 'auto',
                            width: '100%'
                        }}
                    >
                        <Col xs={12} className={"div-con prod-list " + (this.props.isRTL === "rtl" ? "left-align" : "right-align")}>
                            <Row style={this.props.isRTL === "rtl"?{marginRight:"0px",width:"100%"}:{marginLeft:"0px",width:"100%"}}>

                                {toSimulateProductonly && toSimulateProductonly.length > 0 ? (
                                    this.props.content === "product" ?
                                        sortedProducts.map((prod, key) => (
                                            <DataPanelItemCard
                                                prod={prod}
                                                key={key}
                                                cardType={this.props.content}
                                                isRTL={this.props.isRTL}
                                                formValue={valueToPass}
                                                drawRectCanvas={this.props.drawRectCanvas}
                                                dragStart={this.props.dragStart}
                                                dragProdView={this.props.dragProdView}
                                                prodDragEnd={this.props.prodDragEnd}
                                                handleopenDetailmodal={this.props.handleopenDetailmodal}
                                                handlePreviewModal={this.props.handlePreviewModal}
                                                copyToClipboard={this.props.copyToClipboard}
                                                dmode= {this.props.dmode}
                                                filterSelections={this.props.filterSelections}
                                                sortFilter={selectedFilters}
                                                isToggleOn={isToggleOn}
                                                isLoadingLambdaDataPanel={isLoadingLambdaDataPanel}
                                                actualFieldStructures={this.props.actualFieldStructures}
                                            />
                                        )) :
                                        this.props.content === "brand" ?
                                            sortedBrands.map((prod, key) => (
                                                <DataPanelItemCard
                                                    prodBrand={prod}
                                                    key={key}
                                                    cardType={this.props.content}
                                                    isRTL={this.props.isRTL}
                                                    formValue={valueToPass}
                                                    loadingNow={this.props.loadingNow}
                                                    dmode= {this.props.dmode}
                                                    filterSelections={this.props.filterSelections}
                                                    sortFilter={selectedFilters}
                                                    isToggleOn={isToggleOn}
                                                isLoadingLambdaDataPanel={isLoadingLambdaDataPanel}
                                                actualFieldStructures={this.props.actualFieldStructures}
                                                toSimulateProductonly={toSimulateProductonly}
                                                />
                                            )) :
                                            this.props.content === "supplier" ?
                                                sortedSupplier.map((prod, key) => (
                                                    <DataPanelItemCard
                                                        prodSup={prod}
                                                        key={key}
                                                        cardType={this.props.content}
                                                        isRTL={this.props.isRTL}
                                                        formValue={valueToPass}
                                                        loadingNow={this.props.loadingNow}
                                                        dmode= {this.props.dmode}
                                                        filterSelections={this.props.filterSelections}
                                                        sortFilter={selectedFilters}
                                                        isToggleOn={isToggleOn}
                                                isLoadingLambdaDataPanel={isLoadingLambdaDataPanel}
                                                actualFieldStructures={this.props.actualFieldStructures}
                                                toSimulateProductonly={toSimulateProductonly}
                                                    />
                                                )) :
                                            this.props.content === "category" ?
                                                sortedCategory.map((prod, key) => (
                                                    <DataPanelItemCard
                                                        prodCat={prod}
                                                        key={key}
                                                        cardType={this.props.content}
                                                        isRTL={this.props.isRTL}
                                                        formValue={valueToPass}
                                                        loadingNow={this.props.loadingNow}
                                                        dmode= {this.props.dmode}
                                                        filterSelections={this.props.filterSelections}
                                                        sortFilter={selectedFilters}
                                                        isToggleOn={isToggleOn}
                                                isLoadingLambdaDataPanel={isLoadingLambdaDataPanel}
                                                actualFieldStructures={this.props.actualFieldStructures}
                                                toSimulateProductonly={toSimulateProductonly}
                                                    />
                                                )) :
                                                this.props.content === "sub-category" ?
                                                    sortedSubCat.map((prod, key) => (
                                                        <DataPanelItemCard
                                                            prodSubCat={prod}
                                                            key={key}
                                                            cardType={this.props.content}
                                                            isRTL={this.props.isRTL}
                                                            formValue={valueToPass}
                                                            loadingNow={this.props.loadingNow}
                                                            dmode= {this.props.dmode}
                                                            filterSelections={this.props.filterSelections}
                                                            sortFilter={selectedFilters}
                                                            isToggleOn={isToggleOn}
                                                isLoadingLambdaDataPanel={isLoadingLambdaDataPanel}
                                                actualFieldStructures={this.props.actualFieldStructures}
                                                toSimulateProductonly={toSimulateProductonly}
                                                        />
                                                    )) :
                                                    sortedProducts.map((prod, key) => (
                                                        <DataPanelItemCard
                                                            prod={prod}
                                                            key={key}
                                                            cardType={this.props.content}
                                                            isRTL={this.props.isRTL}
                                                            formValue={valueToPass}
                                                            dmode= {this.props.dmode}
                                                            filterSelections={this.props.filterSelections}
                                                            sortFilter={selectedFilters}
                                                            isToggleOn={isToggleOn}
                                                isLoadingLambdaDataPanel={isLoadingLambdaDataPanel}
                                                actualFieldStructures={this.props.actualFieldStructures}
                                                        />
                                                    ))
                                ) : (
                                    <Col xs={12} className="d-flex justify-content-center align-items-center text-color-dark-aui" style={{ height: '100%', paddingTop: '20px' }}>
                                        <p>{this.props.t("No_Data_Available")}</p>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>
                </div>
        );
    }
}
const mapStateToProps = (state, ownProps) => ({
    selectedFilters: state.simulateAll?.dataPanelColumnSelections?.[ownProps.content] || {}
});

const mapDispatchToProps = (dispatch) => ({
    setDataPanelColumnSelection: (tab, selections) => dispatch(setDataPanelColumnSelection(tab, selections))
});

export default withTranslation()(withRouter(connect(mapStateToProps, mapDispatchToProps)(DataPanelTabContent)))